import { useLoyaltyContext } from "../../../services/hooks/loyalty/useLoyaltyContext";
import { useIsLoyaltyOfferInstanceActive } from "../../../services/hooks/loyalty/useLoyaltyOfferInstanceActive";
import { useMenuLookups } from "../../../services/hooks/useMenuLookups";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { removeLoyaltyOffer } from "../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  getMenuSubItemsForOffer,
  useApplyLoyaltyItemOfferFlow,
} from "../../../services/redux/thunks/applyLoyaltyItemOfferFlow";
import { LocalizedString } from "../../../services/redux/types/CartLoyaltyV2Types";
import * as textPreparation from "../../../utils/help/textPreparation/textPreparation";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import styles from "../FlashOfferBanner/FlashOfferBanner.module.scss";
import { BottomRightCornerWave } from "../Icons/Icons";
import { LoyaltyOfferIcon } from "../LoyaltyOfferIcon/LoyaltyOfferIcon";

type LoyaltyFlashOfferBannerProps = {
  offer: LoyaltyOffer;
  offersTab?: boolean;
  /** Sizes the copy as the tiers design does, which sets the banner's height. */
  tierSized?: boolean;
};

export default function LoyaltyFlashOfferBanner({
  offer,
  offersTab,
  tierSized,
}: LoyaltyFlashOfferBannerProps) {
  const { t } = useTranslationI18();
  const dispatch = useAppDispatch();
  const { timeZone, mappedOrderType, channel } = useLoyaltyContext();

  // ------- Selectors -------
  const selectedLanguage = useAppSelector(
    (state) => state.config.selectedLanguage
  );
  const isOfferInstanceActive = useIsLoyaltyOfferInstanceActive();
  const { subItemLookup } = useMenuLookups();
  const applyLoyaltyItemOfferFlow = useApplyLoyaltyItemOfferFlow();
  const partnershipCustomer = useAppSelector(
    (state) => state.loyaltyCustomerInfo?.data?.partnership
  );
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  // ------- Variables -------
  const applicabilityResult = offer.isOfferApplicable({
    timeZone,
    orderType: mappedOrderType ?? undefined,
    channel,
    entityImageLookup,
    itemStatusRedux,
    formatTime: (time) => textPreparation.formatTimeAMPMLocalized(t, time),
  });
  const hasDeletedMenuItems = offer.hasDeletedItems(entityImageLookup);
  const isDisabled = LoyaltyOffer.isLoyaltyOfferBlocked(
    false,
    applicabilityResult.applicable,
    hasDeletedMenuItems
  );
  const unavailableChipText = LoyaltyOffer.getLoyaltyOfferStatusChipText(
    isDisabled,
    applicabilityResult,
    t
  );

  const lang = selectedLanguage as keyof LocalizedString;
  const offerIdentifier = offer.shortId ?? offer.id;

  const isRedeemed = isOfferInstanceActive(offer);
  const description = offer.getDescription(lang);
  const expiryDate = offer.getExpiryDate();
  const isExpiryWithin60Days = offer.isExpiryWithinDays(60);

  const extraPadding = offer.isBogoOffer() || offer.isBundleOffer();

  // ------- Texts -------
  const remove = textPreparation.capitalizeFirstLetter(t("offers:remove"));
  const apply = textPreparation.capitalizeFirstLetter(t("offers:apply"));
  const expDate = t("offers:exp-date");

  // ------- Functions -------
  const createMarkup = (html: string) => ({ __html: html });

  const handleApplyOffer = () => {
    const plain = offer.toPlain();
    applyLoyaltyItemOfferFlow({
      offer: plain,
      menuSubItems: getMenuSubItemsForOffer(plain, subItemLookup),
    });
  };

  const handleRemoveOffer = () => {
    dispatch(removeLoyaltyOffer());
  };

  return (
    <div
      data-testid={`loyalty_offer_slider_${offerIdentifier}`}
      className={`${styles.slide} ${
        partnershipCustomer && styles.organization_offer
      }
        ${isRedeemed && styles.active_slide}
          ${"bg__12"} ${isDisabled ? styles.unavailable : ""} ${
        extraPadding && !offersTab && styles.extra_margin
      } ${tierSized ? styles.tier_sized : ""} rounded`}
    >
      <div
        className={`${styles.slide_wrapper} ${
          extraPadding && styles.extra_padding
        }`}
      >
        <div className={styles.slide_details}>
          {/* ----------------------flash offers carry the primary identity, never the customer's tier colour----------------------- */}
          <LoyaltyOfferIcon
            offer={offer}
            sliderStyle
            backgroundIconColor="primary"
          />
          <div className={`${styles.offer_details}`}>
            <div className={styles.offer_action}>
              {!isRedeemed ? (
                !isDisabled && (
                  <button
                    className={styles.offer_action__btn}
                    onClick={handleApplyOffer}
                    data-testid={`loyalty_apply_offer_${offerIdentifier}`}
                  >
                    {apply}
                  </button>
                )
              ) : (
                <button
                  className={`${styles.offer_action__btn} ${styles.remove_btn}`}
                  data-testid={`loyalty_remove_offer_${offerIdentifier}`}
                  onClick={handleRemoveOffer}
                >
                  {remove}
                </button>
              )}
            </div>
            <p
              className={styles.offer_title}
              dangerouslySetInnerHTML={createMarkup(
                offer.getTitle(selectedLanguage as keyof LocalizedString)
              )}
            />
            {unavailableChipText && (
              <div
                data-testid="loyalty_slider-offer-unavailable"
                className={styles.unavailable}
              >
                {textPreparation.capitalizeFirstLetter(unavailableChipText)}
              </div>
            )}
            <div className={styles.offer_desc_wrapper}>
              {description && (
                <p className={styles.offer_desc}>
                  <span dangerouslySetInnerHTML={createMarkup(description)} />
                </p>
              )}
              {expiryDate && isExpiryWithin60Days && (
                <p
                  className={styles.expiry_date}
                  data-testid={`loyalty_${offerIdentifier}-expiryDate`}
                >
                  <span>{expDate} </span>
                  {expiryDate}
                </p>
              )}
            </div>
          </div>

          <div className={styles.slider_bottom_svg}>
            <BottomRightCornerWave />
          </div>
        </div>
      </div>
    </div>
  );
}
